"use client";

import { useEffect, useState, type RefObject } from "react";

type UseHeroParallaxVarsResult = {
  reducedMotion: boolean;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const writeVars = (
  node: HTMLElement,
  progress: number,
  mouseX: number,
  mouseY: number,
) => {
  node.style.setProperty("--p", progress.toFixed(4));
  node.style.setProperty("--mx", mouseX.toFixed(4));
  node.style.setProperty("--my", mouseY.toFixed(4));
};

export function useHeroParallaxVars(
  ref: RefObject<HTMLElement | null>,
): UseHeroParallaxVarsResult {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReducedMotion(mediaQuery.matches);

    updateMotion();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMotion);
      return () => mediaQuery.removeEventListener("change", updateMotion);
    }

    mediaQuery.addListener(updateMotion);
    return () => mediaQuery.removeListener(updateMotion);
  }, []);

  useEffect(() => {
    const stage = ref.current;
    if (!stage || typeof window === "undefined") {
      return;
    }

    let isDisposed = false;
    let frameId = 0;
    let isInView = true;

    let targetProgress = 0.35;
    let currentProgress = 0.35;
    let targetMouseX = 0;
    let targetMouseY = 0;
    let currentMouseX = 0;
    let currentMouseY = 0;

    let stopNativeTracking: (() => void) | undefined;
    let stopGsapTracking: (() => void) | undefined;

    const requestFrame = () => {
      if (frameId !== 0) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        currentProgress += (targetProgress - currentProgress) * 0.12;
        currentMouseX += (targetMouseX - currentMouseX) * 0.14;
        currentMouseY += (targetMouseY - currentMouseY) * 0.14;
        writeVars(stage, currentProgress, currentMouseX, currentMouseY);

        const delta =
          Math.abs(targetProgress - currentProgress) +
          Math.abs(targetMouseX - currentMouseX) +
          Math.abs(targetMouseY - currentMouseY);

        if (!isDisposed && delta > 0.0025) {
          requestFrame();
        }
      });
    };

    const readProgress = () => {
      const rect = stage.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const rawProgress =
        (viewportHeight - rect.top) / (rect.height + viewportHeight);
      targetProgress = clamp(rawProgress, 0, 1);
      requestFrame();
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = stage.getBoundingClientRect();
      const localX = (event.clientX - rect.left) / rect.width;
      const localY = (event.clientY - rect.top) / rect.height;
      targetMouseX = clamp((localX - 0.5) * 2, -1, 1);
      targetMouseY = clamp((localY - 0.5) * 2, -1, 1);
      requestFrame();
    };

    const onPointerLeave = () => {
      targetMouseX = 0;
      targetMouseY = 0;
      requestFrame();
    };

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        isInView = Boolean(entry?.isIntersecting);
        if (isInView) {
          readProgress();
        }
      },
      {
        threshold: [0, 0.1, 0.35, 0.6, 1],
      },
    );
    intersectionObserver.observe(stage);

    stage.addEventListener("pointermove", onPointerMove);
    stage.addEventListener("pointerleave", onPointerLeave);

    if (reducedMotion) {
      targetProgress = 0.55;
      currentProgress = 0.55;
      targetMouseX = 0;
      targetMouseY = 0;
      currentMouseX = 0;
      currentMouseY = 0;
      writeVars(stage, currentProgress, currentMouseX, currentMouseY);
      return () => {
        isDisposed = true;
        if (frameId !== 0) {
          window.cancelAnimationFrame(frameId);
        }
        intersectionObserver.disconnect();
        stage.removeEventListener("pointermove", onPointerMove);
        stage.removeEventListener("pointerleave", onPointerLeave);
      };
    }

    const setupNativeTracking = () => {
      const onScroll = () => {
        if (!isInView) {
          return;
        }
        readProgress();
      };

      const onResize = () => readProgress();

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize);
      readProgress();

      stopNativeTracking = () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
      };
    };

    const setupGsapTracking = async () => {
      try {
        type GsapLike = {
          registerPlugin: (plugin: unknown) => void;
        };
        type ScrollTriggerLike = {
          create: (config: {
            trigger: HTMLElement;
            start: string;
            end: string;
            scrub: number;
            onUpdate: (self: { progress: number }) => void;
          }) => { refresh: () => void; kill: () => void };
        };
        const dynamicImport = new Function(
          "moduleId",
          "return import(moduleId)",
        ) as (moduleId: string) => Promise<unknown>;

        const [gsapPackage, scrollTriggerPackage] = await Promise.all([
          dynamicImport("gsap"),
          dynamicImport("gsap/ScrollTrigger"),
        ]);

        const gsapExports = gsapPackage as {
          gsap?: GsapLike;
          default?: GsapLike;
        };
        const scrollTriggerExports = scrollTriggerPackage as {
          ScrollTrigger?: ScrollTriggerLike;
          default?: ScrollTriggerLike;
        };
        const gsap = gsapExports.gsap ?? gsapExports.default;
        const ScrollTrigger =
          scrollTriggerExports.ScrollTrigger ?? scrollTriggerExports.default;

        if (
          isDisposed ||
          typeof gsap?.registerPlugin !== "function" ||
          typeof ScrollTrigger?.create !== "function"
        ) {
          return false;
        }

        gsap.registerPlugin(ScrollTrigger);
        const trigger = ScrollTrigger.create({
          trigger: stage,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.5,
          onUpdate: (self: { progress: number }) => {
            targetProgress = clamp(self.progress, 0, 1);
            requestFrame();
          },
        });

        const onResize = () => trigger.refresh();
        window.addEventListener("resize", onResize);
        readProgress();

        stopGsapTracking = () => {
          window.removeEventListener("resize", onResize);
          trigger.kill();
        };

        return true;
      } catch {
        return false;
      }
    };

    void (async () => {
      const gsapAttached = await setupGsapTracking();
      if (!gsapAttached && !isDisposed) {
        setupNativeTracking();
      }
    })();

    return () => {
      isDisposed = true;
      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }
      stopNativeTracking?.();
      stopGsapTracking?.();
      intersectionObserver.disconnect();
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [ref, reducedMotion]);

  return { reducedMotion };
}
